import { isValidYouTubeUrl, convertToEmbedUrl, getYouTubeThumbnailUrl, normalizeYouTubeUrl } from './youtubeUtils';
import { isValidVimeoUrl, convertToVimeoEmbedUrl } from './vimeoUtils';
import { isValidDailymotionUrl, convertToDailymotionEmbedUrl } from './dailymotionUtils';

export const VIDEO_SERVICES = {
  YOUTUBE: 'youtube',
  VIMEO: 'vimeo',
  DAILYMOTION: 'dailymotion',
  UNKNOWN: 'unknown'
};

/**
 * Detects which video service a URL belongs to
 * @param {string} url - The video URL to check
 * @returns {string} - The service name (youtube, vimeo, dailymotion, or unknown)
 */
export const detectVideoService = (url) => {
  if (!url) return VIDEO_SERVICES.UNKNOWN;

  if (isValidYouTubeUrl(url)) {
    return VIDEO_SERVICES.YOUTUBE;
  }

  if (isValidVimeoUrl(url)) {
    return VIDEO_SERVICES.VIMEO;
  }

  if (isValidDailymotionUrl(url)) {
    return VIDEO_SERVICES.DAILYMOTION;
  }

  return VIDEO_SERVICES.UNKNOWN;
};

/**
 * Validates if a URL is from any supported video service
 * @param {string} url - The video URL to validate
 * @returns {boolean} - True if URL is from a supported service
 */
export const isValidVideoUrl = (url) => {
  const service = detectVideoService(url);
  return service !== VIDEO_SERVICES.UNKNOWN;
};

/**
 * Converts any supported video URL to its embed format
 * @param {string} url - The video URL to convert
 * @returns {string} - The embed URL
 */
export const convertToVideoEmbedUrl = (url) => {
  const service = detectVideoService(url);

  switch (service) {
    case VIDEO_SERVICES.YOUTUBE:
      return convertToEmbedUrl(url);
    case VIDEO_SERVICES.VIMEO:
      return convertToVimeoEmbedUrl(url);
    case VIDEO_SERVICES.DAILYMOTION:
      return convertToDailymotionEmbedUrl(url);
    default:
      return url;
  }
};

/**
 * Gets fallback thumbnail URL for a video (YouTube only, others use backend metadata)
 * @param {string} url - The video URL
 * @returns {string|null} - The thumbnail URL or null
 */
export const getFallbackThumbnailUrl = (url) => {
  const service = detectVideoService(url);

  if (service === VIDEO_SERVICES.YOUTUBE) {
    return getYouTubeThumbnailUrl(url);
  }

  // Vimeo and Dailymotion require backend metadata for thumbnails
  return null;
};

/**
 * Normalizes a video URL to its canonical format
 * For YouTube, converts shorts/embed/youtu.be URLs to standard watch format
 * @param {string} url - The video URL to normalize
 * @returns {string} - The normalized URL
 */
export const normalizeVideoUrl = (url) => {
  if (!url) return url;

  const service = detectVideoService(url);

  if (service === VIDEO_SERVICES.YOUTUBE) {
    return normalizeYouTubeUrl(url);
  }

  return url;
};

/**
 * Gets the display name for a video service
 * @param {string} service - The service identifier
 * @returns {string} - The display name
 */
export const getServiceDisplayName = (service) => {
  switch (service) {
    case VIDEO_SERVICES.YOUTUBE:
      return 'YouTube';
    case VIDEO_SERVICES.VIMEO:
      return 'Vimeo';
    case VIDEO_SERVICES.DAILYMOTION:
      return 'Dailymotion';
    default:
      return 'Unknown';
  }
};
